import React from 'react';
import { CanvasEdgeLayout, CanvasLayoutMode } from '../../types/canvas';
import { formatNumber } from '../../utils/formatters';

interface ConnectorLineProps {
  edge: CanvasEdgeLayout;
  layoutMode: CanvasLayoutMode;
  treeOrientation?: 'vertical' | 'horizontal';
  isHighlighted?: boolean;
  isDormant?: boolean;
  showBadge?: boolean;
}

export const ConnectorLine: React.FC<ConnectorLineProps> = ({
  edge,
  layoutMode,
  treeOrientation = 'vertical',
  isHighlighted = false,
  isDormant = false,
  showBadge = true,
}) => {
  const { sourceX, sourceY, targetX, targetY, netRateDifferenceUAH } = edge;

  let pathData = '';
  let midX = (sourceX + targetX) / 2;
  let midY = (sourceY + targetY) / 2;

  if (layoutMode === 'tree') {
    const isSourceRoot = edge.sourceIsRoot ?? (sourceY === 0 && sourceX === 0);

    if (treeOrientation === 'vertical') {
      const startX = sourceX;
      const startY = sourceY + (isSourceRoot ? 64 : 58);
      const endX = targetX;
      const endY = targetY - 24;

      if (Math.abs(endX - startX) < 1) {
        pathData = `M ${startX} ${startY} L ${endX} ${endY}`;
        midX = endX;
        midY = (startY + endY) / 2;
      } else {
        const busY = startY + (endY - startY) * 0.45;
        const dirX = endX > startX ? 1 : -1;
        const cornerR = Math.min(14, Math.abs(endX - startX) / 2, Math.abs(busY - startY) * 0.8);

        pathData = [
          `M ${startX} ${startY}`,
          `L ${startX} ${busY - cornerR}`,
          `Q ${startX} ${busY}, ${startX + dirX * cornerR} ${busY}`,
          `L ${endX - dirX * cornerR} ${busY}`,
          `Q ${endX} ${busY}, ${endX} ${busY + cornerR}`,
          `L ${endX} ${endY}`,
        ].join(' ');

        // Position badge on the branch drop down to child
        midX = endX;
        midY = Math.round(busY + cornerR + (endY - (busY + cornerR)) * 0.5);
      }
    } else {
      const startX = sourceX + (isSourceRoot ? 29 : 24);
      const startY = sourceY;
      const endX = targetX - 24;
      const endY = targetY;

      if (Math.abs(endY - startY) < 1) {
        pathData = `M ${startX} ${startY} L ${endX} ${endY}`;
        midX = (startX + endX) / 2;
        midY = endY;
      } else {
        const busX = startX + (endX - startX) * 0.45;
        const dirY = endY > startY ? 1 : -1;
        const cornerR = Math.min(14, Math.abs(endY - startY) / 2, Math.abs(busX - startX) * 0.8);

        pathData = [
          `M ${startX} ${startY}`,
          `L ${busX - cornerR} ${startY}`,
          `Q ${busX} ${startY}, ${busX} ${startY + dirY * cornerR}`,
          `L ${busX} ${endY - dirY * cornerR}`,
          `Q ${busX} ${endY}, ${busX + cornerR} ${endY}`,
          `L ${endX} ${endY}`,
        ].join(' ');

        // Position badge on the branch entry into child, safely spaced before the child circle
        midX = Math.round(busX + cornerR + (endX - (busX + cornerR)) * 0.45);
        midY = endY;
      }
    }
  } else {
    // Radial Polar Layout
    const r1 = Math.sqrt(sourceX * sourceX + sourceY * sourceY);
    const r2 = Math.sqrt(targetX * targetX + targetY * targetY);

    if (r1 < 5) {
      // Direct from center root
      const cpX = targetX * 0.5;
      const cpY = targetY * 0.5;
      pathData = `M ${sourceX} ${sourceY} Q ${cpX} ${cpY}, ${targetX} ${targetY}`;
      midX = targetX * 0.55;
      midY = targetY * 0.55;
    } else {
      const theta1 = Math.atan2(sourceY, sourceX);
      const theta2 = Math.atan2(targetY, targetX);
      const rMid = (r1 + r2) / 2;

      const cp1X = rMid * Math.cos(theta1);
      const cp1Y = rMid * Math.sin(theta1);
      const cp2X = rMid * Math.cos(theta2);
      const cp2Y = rMid * Math.sin(theta2);

      pathData = `M ${sourceX} ${sourceY} C ${cp1X} ${cp1Y}, ${cp2X} ${cp2Y}, ${targetX} ${targetY}`;
      midX = (cp1X + cp2X) / 2;
      midY = (cp1Y + cp2Y) / 2;
    }
  }

  // Visual styling
  let strokeColor = '#10B981'; // Emerald
  let strokeWidth = 1.8;
  let strokeDash = undefined;
  let strokeOpacity = 0.75;

  if (isHighlighted) {
    strokeColor = '#06B6D4'; // Cyan
    strokeWidth = 2.8;
    strokeOpacity = 1;
  } else if (isDormant) {
    strokeColor = '#475569'; // Slate
    strokeWidth = 1.2;
    strokeDash = '4 4';
    strokeOpacity = 0.45;
  }

  const badgeText = `+${formatNumber(netRateDifferenceUAH, 2)} ₴/Е`;
  const dx = targetX - sourceX;
  const dy = targetY - sourceY;
  const lineDistance = Math.sqrt(dx * dx + dy * dy);
  const shouldShowBadge = showBadge && (layoutMode === 'tree' || lineDistance >= 115);
  // SVG filters with default objectBoundingBox collapse to 0x0 on 1D lines (dx === 0 or dy === 0).
  // Only apply filter when path has 2D bounding box; otherwise dual-stroke halo provides crisp glow.
  const has2DBox = Math.abs(dx) > 1 && Math.abs(dy) > 1;
  const glowFilter = isHighlighted && has2DBox ? 'url(#neon-glow-cyan)' : undefined;

  return (
    <g className="connector-group">
      {/* Glow path: Wide blurred halo rendered underneath for highlighted lines */}
      {isHighlighted && (
        <path
          d={pathData}
          fill="none"
          stroke="#06B6D4"
          strokeWidth={strokeWidth + 4}
          strokeOpacity={0.35}
          strokeLinecap="round"
          strokeLinejoin="round"
          filter={glowFilter}
          className="pointer-events-none"
        />
      )}
      {/* Primary connector stroke */}
      <path
        d={pathData}
        fill="none"
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        strokeDasharray={strokeDash}
        strokeOpacity={strokeOpacity}
        strokeLinecap="round"
        strokeLinejoin="round"
        className="transition-all duration-300 pointer-events-none"
      />

        {/* Midpoint Rate Difference Badge */}
        {shouldShowBadge && (
          <g
            transform={`translate(${midX}, ${midY})`}
            className="cursor-default select-none pointer-events-none"
          >
            <rect
              x="-30"
              y="-8.5"
              width="60"
              height="17"
              rx="5"
              fill="#0A0F1D"
              fillOpacity="0.94"
              stroke={isHighlighted ? '#06B6D4' : isDormant ? '#334155' : '#10B981'}
              strokeWidth="0.9"
              strokeOpacity={isHighlighted ? 1 : isDormant ? 0.4 : 0.85}
            />
            <text
              x="0"
              y="2.8"
              textAnchor="middle"
              fontSize="8"
              fontWeight="700"
              fill={isHighlighted ? '#06B6D4' : isDormant ? '#64748B' : '#00E599'}
              fontFamily="Inter, system-ui, sans-serif"
            >
              {badgeText}
            </text>
          </g>
        )}
      </g>
    );
  };
